import { ColumnDef } from '@tanstack/react-table'
import { CantileverButton } from 'components/CantileverButton'
import { OcrResultWithPage } from 'features/knowledgeDocumentViewer/hooks/useOcrResults'
import { ArrowRight, Flag, Loader2, Search } from 'lucide-react'
import React from 'react'
import styled, { keyframes } from 'styled-components'
import { DocumentViewerAssetType } from 'types'

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const LoadingSpinner = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`

const TextCell = styled.span`
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TypeCell = styled.span`
  text-transform: capitalize;
`

const RefsCell = styled.span`
  text-align: center;
`

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
`

export const formatAssetType = (
  type: DocumentViewerAssetType | undefined
): string => {
  if (!type) return '–'
  return type
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export type OcrTableColumnsProps = {
  ocrResultRefsMap: Map<string, number>
  isLoadingRefs?: boolean
  onZoomToBox: (result: OcrResultWithPage) => void
  onFlagClick: (result: OcrResultWithPage) => void
  onViewLinkedClick: (
    result: OcrResultWithPage,
    event: React.MouseEvent
  ) => void
  onResultHover?: (result: OcrResultWithPage | null) => void
}

export const createOcrTableColumns = ({
  ocrResultRefsMap,
  isLoadingRefs,
  onZoomToBox,
  onFlagClick,
  onViewLinkedClick,
  onResultHover,
}: OcrTableColumnsProps): ColumnDef<OcrResultWithPage>[] => [
  {
    accessorKey: 'text',
    header: 'Text',
    cell: ({ row }) => (
      <TextCell title={row.original.text}>{row.original.text || '–'}</TextCell>
    ),
  },
  {
    accessorKey: 'type',
    header: 'Type',
    cell: ({ row }) => (
      <TypeCell>{formatAssetType(row.original.type)}</TypeCell>
    ),
  },
  {
    id: 'refs',
    header: 'Refs',
    accessorFn: (row) => {
      const key = `${row.id}-${row.pageNumber}`
      return ocrResultRefsMap.get(key) ?? 0
    },
    sortingFn: 'basic',
    cell: ({ row }) => {
      if (isLoadingRefs) {
        return (
          <RefsCell>
            <LoadingSpinner size={12} />
          </RefsCell>
        )
      }
      const key = `${row.original.id}-${row.original.pageNumber}`
      const refsCount = ocrResultRefsMap.get(key) ?? 0
      return <RefsCell>{refsCount}</RefsCell>
    },
  },
  {
    id: 'actions',
    header: '',
    cell: ({ row }) => {
      const result = row.original
      const key = `${result.id}-${result.pageNumber}`
      const refsCount = ocrResultRefsMap.get(key) ?? 0
      const hasLinkedDocs = refsCount > 0
      const isOffPageConnector =
        result.type === DocumentViewerAssetType.OFF_PAGE_CONNECTOR
      const canNavigate =
        isOffPageConnector || (!isLoadingRefs && hasLinkedDocs)

      return (
        <ActionsContainer
          onMouseEnter={() => onResultHover?.(result)}
          onMouseLeave={() => onResultHover?.(null)}>
          <CantileverButton
            ghost
            iconOnly
            title='Report Issue'
            onClick={() => onFlagClick(result)}>
            <Flag width={16} height={16} />
          </CantileverButton>
          <CantileverButton
            ghost
            iconOnly
            title='Zoom to box'
            onClick={() => onZoomToBox(result)}>
            <Search width={16} height={16} />
          </CantileverButton>
          <CantileverButton
            ghost
            iconOnly
            title={
              isOffPageConnector
                ? 'Navigate to linked document'
                : 'View linked documents'
            }
            disabled={!canNavigate}
            onClick={(e) => onViewLinkedClick(result, e)}>
            <ArrowRight width={16} height={16} />
          </CantileverButton>
        </ActionsContainer>
      )
    },
    enableHiding: false,
  },
]
